import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import CustomSelectBox from "../common/CustomSelectBox";
import AdminDialog from "./AdminDialog";
import CustomerSpecialtiesDetail from "./CustomerSpecialtiesDetail";
import {
  getCustomerServices,
  createCustomerService,
  deleteCustomerService
} from "../../redux/customerServices/thunks";
import {
  selectCustomerServicesByCustomer,
  selectCustomerServicesLoading
} from "../../redux/customerServices/selectors";
import {
  getCustomerPricings
} from "../../redux/customerPricing/thunks";
import {
  selectCustomerPricingsByCustomer
} from "../../redux/customerPricing/selectors";
import { getSupplierServiceTypes } from "../../redux/supplierServiceTypes/thunks";
import { selectSupplierServiceTypes } from "../../redux/supplierServiceTypes/selectors";
import { openModal } from "../../redux/modal/modalSlice";
import AddIcon from "@material-ui/icons/Add";
import deleteIcon from "../../images/delete.svg";
import arrowDown from "../../images/arrow-down.svg";

const useStyles = makeStyles({
  wrapper: {
    padding: "1rem 1.25rem 1.25rem 1.25rem",
    backgroundColor: "#f8fafc",
    borderTop: "1px solid #cbd5e1",
    borderBottom: "1px solid #cbd5e1"
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    marginBottom: "1rem",
    flexWrap: "wrap"
  },
  titleArea: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem"
  },
  title: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#1e293b"
  },
  actionsArea: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap"
  },
  filterSelect: {
    minWidth: "200px"
  },
  servicesList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem"
  },
  accordion: {
    border: "1px solid #e2e8f0",
    borderRadius: "0 !important",
    boxShadow: "none",
    "&:before": {
      display: "none"
    }
  },
  accordionSummary: {
    backgroundColor: "#ffffff",
    borderRadius: 0,
    minHeight: "44px !important",
    "&.Mui-expanded": {
      minHeight: "44px !important",
      backgroundColor: "#f1f5f9"
    }
  },
  summaryContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingRight: "1rem"
  },
  summaryActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem"
  },
  serviceTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#334155"
  },
  chip: {
    backgroundColor: "#e2e8f0",
    color: "#475569",
    fontWeight: 500,
    fontSize: "0.75rem"
  },
  deleteButton: {
    padding: "4px",
    marginLeft: "4px"
  },
  accordionDetails: {
    padding: "0.5rem 0.75rem 0.75rem 0.75rem",
    backgroundColor: "#ffffff"
  },
  emptyText: {
    color: "#64748b",
    fontSize: "0.9rem",
    padding: "1rem 0"
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "1.5rem"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0.85rem",
    padding: "0.5rem 0"
  },
  deleteConfirmText: {
    fontSize: "0.95rem",
    padding: "1rem 0"
  }
});

function CustomerServicesDetail(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const customer = props.data?.data || props.data || {};
  const customerId = customer.customerId;
  const customerName = customer.customerName || customer.name || "";

  const [selectedBillingModel, setSelectedBillingModel] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState("");
  const [dialogBillingModel, setDialogBillingModel] = useState("DEMANDA");
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const servicesFromStore = useSelector((state) =>
    selectCustomerServicesByCustomer(state, customerId)
  );
  const servicesLoading = useSelector(selectCustomerServicesLoading);
  const pricings = useSelector((state) =>
    selectCustomerPricingsByCustomer(state, customerId)
  );
  const serviceTypes = useSelector(selectSupplierServiceTypes);

  const billingModelOptions = useMemo(() => [
    { id: "", text: t("AllBillingModels") },
    { id: "DEMANDA", text: t("BillingModelDemand") },
    { id: "FREQUENCIA", text: t("BillingModelFrequency") },
    { id: "CADENCIA", text: t("BillingModelCadence") },
    { id: "PRE-CADENCIA", text: t("BillingModelPreCadence") },
    { id: "AGUARDANDO", text: t("BillingModelPending") }
  ], [t]);

  const dialogBillingModelOptions = useMemo(() => [
    { id: "DEMANDA", text: t("BillingModelDemand") },
    { id: "FREQUENCIA", text: t("BillingModelFrequency") },
    { id: "CADENCIA", text: t("BillingModelCadence") },
    { id: "PRE-CADENCIA", text: t("BillingModelPreCadence") },
    { id: "AGUARDANDO", text: t("BillingModelPending") }
  ], [t]);

  const loadData = () => {
    if (customerId) {
      const query = selectedBillingModel ? `?billingModel=${selectedBillingModel}` : "";
      dispatch(getCustomerServices({ param: customerId, query }));
      dispatch(getCustomerPricings({ param: customerId, query }));
    }
  };

  useEffect(() => {
    loadData();
    if (!serviceTypes || serviceTypes.length === 0) {
      dispatch(getSupplierServiceTypes());
    }
  }, [dispatch, customerId, selectedBillingModel]);

  const customerServices = useMemo(() => {
    if (servicesFromStore && servicesFromStore.length > 0) {
      return servicesFromStore.map((s) => {
        const matchingPricings = (pricings || []).filter(
          (p) =>
            Number(p.supplierServiceTypeId) === Number(s.supplierServiceTypeId) &&
            (!s.billingModel || !p.billingModel || p.billingModel.toUpperCase() === s.billingModel.toUpperCase())
        );
        const specialties = matchingPricings.length > 0 ? matchingPricings : (s.specialties || []);
        return {
          ...s,
          specialties,
          specialtiesCount: specialties.length
        };
      });
    }

    const serviceMap = new Map();
    (pricings || []).forEach((p) => {
      const key = `${p.supplierServiceTypeId}_${p.billingModel || "DEMANDA"}`;
      if (!serviceMap.has(key)) {
        serviceMap.set(key, {
          customerId: customerId,
          supplierServiceTypeId: p.supplierServiceTypeId,
          serviceTypeName: p.serviceTypeName || "",
          billingModel: p.billingModel || "DEMANDA",
          specialties: [p],
          specialtiesCount: 1
        });
      } else {
        const item = serviceMap.get(key);
        item.specialties.push(p);
        item.specialtiesCount += 1;
      }
    });
    return Array.from(serviceMap.values());
  }, [servicesFromStore, pricings, customerId]);

  const handleOpenCreateService = () => {
    setSelectedServiceTypeId("");
    setDialogBillingModel("DEMANDA");
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setSelectedServiceTypeId("");
    setDialogBillingModel("DEMANDA");
  };

  const handleSaveService = () => {
    if (!selectedServiceTypeId || !dialogBillingModel) {
      dispatch(openModal({ type: "warning", message: t("PleaseFillAllFields") }));
      return;
    }

    const payload = {
      customerId: customerId,
      supplierServiceTypeId: Number(selectedServiceTypeId),
      billingModel: dialogBillingModel
    };

    dispatch(createCustomerService({ body: payload })).then((res) => {
      if (res.error) {
        dispatch(
          openModal({
            type: "warning",
            message: res.error.message || t("ServiceAlreadyLinked")
          })
        );
      } else {
        loadData();
        handleCloseCreateDialog();
      }
    });
  };

  const handleOpenDeleteService = (service, event) => {
    if (event) {
      event.stopPropagation();
    }
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleConfirmDeleteService = () => {
    if (!serviceToDelete) {
      return;
    }

    if (serviceToDelete.customerServiceId) {
      dispatch(deleteCustomerService({ param: serviceToDelete.customerServiceId })).then(() => {
        loadData();
        handleCloseDeleteDialog();
      });
    } else {
      handleCloseDeleteDialog();
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.titleArea}>
          <Typography className={classes.title}>
            {t("AvailableServices")} - {customerName}
          </Typography>
        </div>
        <div className={classes.actionsArea}>
          <div className={classes.filterSelect}>
            <CustomSelectBox
              label={t("BillingModel")}
              labelMode="floating"
              value={selectedBillingModel}
              dataSource={billingModelOptions}
              valueExpr="id"
              displayExpr="text"
              onValueChange={(val) => setSelectedBillingModel(val || "")}
            />
          </div>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateService}
          >
            {t("LinkNewService")}
          </Button>
        </div>
      </div>

      {servicesLoading === "loading" && customerServices.length === 0 ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </div>
      ) : customerServices.length === 0 ? (
        <Typography className={classes.emptyText}>
          {t("NoServicesFoundForCustomer")}
        </Typography>
      ) : (
        <div className={classes.servicesList}>
          {customerServices.map((service, index) => (
            <Accordion
              key={service.customerServiceId || `${service.supplierServiceTypeId}_${service.billingModel}_${index}`}
              className={classes.accordion}
              TransitionProps={{ unmountOnExit: true }}
            >
              <AccordionSummary
                expandIcon={
                  <img
                    style={{ width: "0.75rem", height: "0.75rem" }}
                    src={arrowDown}
                    alt=""
                  />
                }
                className={classes.accordionSummary}
              >
                <div className={classes.summaryContent}>
                  <Typography className={classes.serviceTitle}>
                    {service.serviceTypeName} ({service.billingModel || "DEMANDA"})
                  </Typography>
                  <div className={classes.summaryActions}>
                    <Chip
                      label={`${service.specialtiesCount} ${t("Specialties")}`}
                      size="small"
                      className={classes.chip}
                    />
                    <IconButton
                      size="small"
                      className={classes.deleteButton}
                      onClick={(e) => handleOpenDeleteService(service, e)}
                      title={t("UnlinkService")}
                    >
                      <img height={16} src={deleteIcon} alt={t("UnlinkService")} />
                    </IconButton>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <CustomerSpecialtiesDetail
                  customerId={customerId}
                  customerServiceId={service.customerServiceId}
                  service={service}
                  specialties={service.specialties || []}
                  billingModel={service.billingModel}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      )}

      {createDialogOpen && (
        <AdminDialog
          open={createDialogOpen}
          title={t("LinkNewService")}
          onClose={handleCloseCreateDialog}
          handleOnClick={handleSaveService}
          dialogSize={{ width: "450px", height: "auto" }}
        >
          <div className={classes.formGrid}>
            <CustomSelectBox
              label={t("ServiceType")}
              labelMode="floating"
              value={selectedServiceTypeId}
              dataSource={serviceTypes || []}
              valueExpr="supplierServiceTypeId"
              displayExpr="description"
              onValueChange={(val) => setSelectedServiceTypeId(val)}
            />
            <CustomSelectBox
              label={t("BillingModel")}
              labelMode="floating"
              value={dialogBillingModel}
              dataSource={dialogBillingModelOptions}
              valueExpr="id"
              displayExpr="text"
              onValueChange={(val) => setDialogBillingModel(val)}
            />
          </div>
        </AdminDialog>
      )}

      {deleteDialogOpen && (
        <AdminDialog
          operation="delete"
          open={deleteDialogOpen}
          title={t("UnlinkService")}
          onClose={handleCloseDeleteDialog}
          handleOnClick={handleConfirmDeleteService}
        >
          <Typography className={classes.deleteConfirmText}>
            {t("DeleteCustomerServiceConfirm")}
          </Typography>
        </AdminDialog>
      )}
    </div>
  );
}

export default CustomerServicesDetail;